<style scoped>
.damage {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-shadow:
        -1px -1px 0 #212121,
        1px -1px 0 #212121,
        -1px 1px 0 #212121,
        1px 1px 0 #212121,
        6px 6px 0 #212121;
}
</style>
<template>
    <div
        class="flex-1 w-full bg-gray-100 bg-no-repeat bg-center bg-cover"
        :style="`background-image: url(${battle.terrain.image_path});`"
    >
        <div class="inset-0 md:grid md:grid-cols-3 md:gap-6">
            <div class="p-4">
                <div class="w-full relative">
                    <img class="w-full" :src="battle.enemy.image_path" alt="enemy" ref="enemy" />

                    <div class="w-full flex justify-center absolute bottom-0">
                        <span class="text text-red-600 text-9xl damage z-10" ref="damage" id="damage">{{
                            damage
                        }}</span>
                    </div>
                </div>
                <ProgressBar :percent="percent" color="red">
                    <div>{{ life }} / {{ battle.enemy_health_total }}</div>
                </ProgressBar>
            </div>
            <div class="col-span-2 order-first">
                <div class="h-full flex flex-col justify-end p-4">
                    <div
                        class="flex"
                        v-for="(student, index) in battle.actions"
                        :key="student.id"
                        :id="`group_${index}`"
                        style="display: none"
                    >
                        <div>
                            <img
                                class="w-24 h-24 rounded-full"
                                :src="student.avatar_or_default"
                                :id="`student_${index}`"
                                alt=""
                                ref="student"
                            />
                            <div class="flex justify-center text-white">
                                <span class="bg-gray-800 bg-opacity-75 px-2 py-1 rounded-lg mt-3">
                                    {{ student.name_or_nickname }}
                                </span>
                            </div>
                        </div>
                        <img
                            class="w-auto h-48 rounded-full"
                            :src="student.action_image"
                            :id="`student_weapon_${index}`"
                            ref="weapon"
                            alt=""
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="flex justify-center w-full space-x-2">
            <AppButton desired-action @click="play()" element="button" v-show="playing === false">
                {{ $t('messages.start_battle') }}
            </AppButton>
            <span class="bg-gray-800 bg-opacity-75 px-2 py-1 rounded-lg mt-3 text-white" v-show="finished === false">
                {{ position + 1 }} / {{ battle.actions.length }}
            </span>
        </div>
        <Modal :show="showModal" @close="showModal = false">
            <template v-if="life === 0">
                <div class="flex justify-center">
                    <h2 class="inline-block text-5xl">
                        {{ $t('messages.victory!') }}
                    </h2>
                </div>
                <div class="flex justify-center">
                    <svg
                        width="48"
                        height="48"
                        class="text-yellow-400 h-20 w-20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M13.5899 12.76C13.4699 13.07 13.3599 13.46 13.3099 13.89C13.2599 14.28 13.2699 14.63 13.3099 14.92C13.3599 15.36 13.4699 15.75 13.5999 16.06C13.3999 16.02 13.1999 16 12.9999 16H10.9999C10.7999 16 10.5999 16.02 10.3999 16.06C10.5299 15.75 10.6399 15.36 10.6899 14.92C10.7299 14.63 10.7399 14.28 10.6899 13.89C10.6399 13.46 10.5299 13.07 10.4099 12.76C10.9199 12.92 11.4499 13 11.9999 13C12.5499 13 13.0799 12.92 13.5899 12.76Z"
                            mask="url(#mask_100_101)"
                        ></path>
                        <path
                            d="M16 19H8C8 17.34 9.34 16 11 16H13C14.66 16 16 17.34 16 19Z"
                            mask="url(#mask_100_101)"
                        ></path>
                        <path
                            d="M19 22H5C5 20.34 6.34 19 8 19H16C17.66 19 19 20.34 19 22Z"
                            mask="url(#mask_100_101)"
                        ></path>
                        <path
                            d="M17.8199 4H20.9999C20.9999 4 20.9599 7.45 16.8799 10C17.1799 9.4 17.3799 8.74 17.4399 8.04L17.8199 4Z"
                            mask="url(#mask_100_101)"
                        ></path>
                        <path
                            d="M6.18 4H3C3 4 3.04 7.45 7.12 10C6.82 9.4 6.62 8.74 6.56 8.04L6.18 4Z"
                            mask="url(#mask_100_101)"
                        ></path>
                        <path
                            d="M12 13C9.18 13 6.82 10.85 6.56 8.04L6 2H18L17.44 8.04C17.18 10.85 14.82 13 12 13Z"
                            mask="url(#mask_100_101)"
                        ></path>
                        <mask id="mask_100_101">
                            <rect width="100%" height="100%" fill="white"></rect>
                        </mask>
                    </svg>
                </div>
                <div>
                    {{ $t('messages.prize_to_be_shared') }}:
                    <div class="flex space-x-2">
                        <div>
                            <img class="h-6 w-6" src="/assets/gamification/currency/coin.svg" alt="emerald" />
                        </div>
                        <div>
                            {{ battle.coins_amount }}
                        </div>
                    </div>
                    <div class="mt-5">
                        {{ rewardMessage }}
                    </div>
                </div>
                <div class="mt-5 italic text-gray-700 text-2xl">
                    <p>{{ battle.victory_text }}</p>
                </div>
            </template>
            <template v-else>
                <div class="flex justify-center">
                    <h2 class="inline-block text-5xl">
                        {{ $t('messages.defeat!') }}
                    </h2>
                </div>
                <div class="mt-5 italic text-gray-700 text-2xl">
                    <p>{{ battle.defeat_text }}</p>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script>
import AppButton from '@/components/AppButton.vue';
import { Howl } from 'howler';
import { random } from 'lodash';

import anime from 'animejs';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';
import Modal from '@/components/Modal.vue';

function getDamageRotation() {
    const number = random(1, 100);
    const isOdd = number % 2 === 0;
    if (number > 85) {
        return isOdd ? '1.05turn' : '-1.05turn';
    }

    return isOdd ? '0.05turn' : '-0.05turn';
}

function animateWeapon(i) {
    return {
        targets: '#student_weapon_' + i,
        loop: false,
        keyframes: [
            {
                opacity: 1,
                duration: 1,
            },
            {
                duration: 1250,
                translateX: 1200,
                translateY: -200,
                scale: 0.5,
                // rotate: '1.1turn',
                direction: 'alternate',
                easing: 'easeInElastic(1, .6)',
            },
        ],
    };
}

export default {
    components: {
        AppButton,
        ProgressBar,
        Modal,
    },
    name: 'BattlePlay',
    props: ['meta', 'battle'],
    data() {
        return {
            life: this.battle.enemy_health_total,
            damage: null,
            position: null,
            showModal: false,
            playing: false,
            finished: false,
            autoPlay: true,
            rewardMessage: '',
        };
    },
    watch: {
        life: function (life) {
            if (life <= 0) {
                this.showEndScreen();
            }
        },
    },
    methods: {
        showEndScreen() {
            if (this.showModal === false) {
                if (this.life <= 0) {
                    this.showVictory();
                } else {
                    this.showDefeat();
                }
            }
        },
        showVictory() {
            setTimeout(() => {
                this.showModal = true;

                this.$nextTick(() => {
                    this.start();
                    const sound = new Howl({
                        src: ['/assets/gamification/victory.mp3'],
                    });
                    sound.play();
                    if (this.battle.should_give_reward) {
                        setTimeout(() => {
                            axios
                                .patch(this.meta.links.update)
                                .then(() => (this.rewardMessage = this.$t('messages.prize_given')));
                        }, 2000);
                    }
                    setTimeout(() => this.$confetti.stop(), 5000);
                });
            }, 1000);
        },
        showDefeat() {
            setTimeout(() => {
                this.showModal = true;

                this.$nextTick(() => {
                    if (this.battle.should_give_reward) {
                        setTimeout(() => {
                            axios.patch(this.meta.links.update).then(() => {});
                        }, 2000);
                    }
                });
            }, 1000);
        },
        start() {
            this.$confetti.start({
                canvasId: 'modal-canvas-background',
            });
        },
        playWeaponSound() {
            if (this.life) {
                setTimeout(() => {
                    const sound = new Howl({
                        src: ['/assets/gamification/clean_fast_swoosh.mp3'],
                    });
                    sound.play();
                }, 700);
            }
        },
        animateStudent(i, action) {
            const tl = anime.timeline({
                begin: (anim) => {
                    document.getElementById('group_' + i).style.display = 'flex';
                    this.playWeaponSound();
                },
                complete: () => {
                    const minus = action.damage;
                    if (this.life >= minus) {
                        this.life -= minus;
                    } else {
                        this.life = 0;
                    }
                },
            });

            tl.add(animateWeapon(i))
                .finished.then(() => {
                    anime({
                        targets: this.$refs.enemy,
                        duration: 500,
                        keyframes: [
                            { translateX: 1, translateY: 1 },
                            { translateX: -1, translateY: -2 },
                            { translateX: -3, translateY: 0 },
                            { translateX: 3, translateY: 2 },
                            { translateX: 1, translateY: -1 },
                            { translateX: -1, translateY: 2 },
                            { translateX: -3, translateY: 1 },
                            { translateX: 3, translateY: 1 },
                            { translateX: -1, translateY: -1 },
                            { translateX: 1, translateY: 2 },
                            { translateX: 1, translateY: -2 },
                        ],
                    });

                    return this.dealDamage(action.damage);

                    // return t2.finished;
                })
                .then(() => {
                    const t3 = anime.timeline({
                        duration: 1000,
                        complete: (anim) => {
                            document.getElementById('group_' + i).style.display = 'none';
                        },
                    });
                    t3.add({
                        // targets: this.$refs.student,
                        targets: '#student_' + i,
                        loop: false,
                        keyframes: [
                            {
                                translateX: -300,
                                translateY: -200,
                                rotate: -90,
                                easing: 'easeInElastic(1, .6)',
                            },
                        ],
                    });
                    return t3.finished;
                })
                .then(() => {
                    this.advance();
                });
        },
        dealDamage(qty) {
            this.damage = qty;
            const tl = anime.timeline({
                begin: function (anim) {
                    document.getElementById('damage').style.display = 'block';
                },
                complete: (anim) => {
                    document.getElementById('damage').style.display = 'none';
                },
            });

            tl.add({
                targets: this.$refs.damage,
                duration: 1200,
                keyframes: [
                    {
                        translateY: 0,
                        scale: 0.5,
                        duration: 1,
                        opacity: 1,
                    },
                    {
                        translateY: -100,
                        scale: 1.5,
                        rotate: getDamageRotation(),
                        // easing: 'easeInElastic(1, .6)',
                    },
                    {
                        duration: 1,
                        opacity: 0,
                    },
                ],
            });
            return tl.finished;
        },
        play() {
            if (this.battle.actions.length > 0) {
                this.playing = true;
                this.position = 0;
                const action = this.battle.actions[this.position];
                this.animateStudent(this.position, action);
            }
        },
        advance() {
            const next = this.position + 1;
            if (this.battle.actions.hasOwnProperty(next)) {
                this.position = next;
                if (this.autoPlay) {
                    this.$nextTick(() => {
                        const action = this.battle.actions[this.position];
                        this.animateStudent(this.position, action);
                    });
                }
            } else {
                this.position = null;
                this.finished = true;
                this.showEndScreen();
            }
        },
    },
    computed: {
        percent: function () {
            return Math.floor((this.life / this.battle.enemy_health_total) * 100);
        },
    },
};
</script>
